import { useState } from 'react';
import { CalendarPlus } from 'lucide-react';
import type { Task } from '../types';
import {
  defaultEventAt,
  eventMinutes,
  googleCalendarUrl,
  icsFileName,
  icsFor,
  lengthOptions,
  type EventChoice,
} from '../engine/calendarExport';
import { isNative } from '../native/native';

/** Quick start times (24-hour, as the picker shows them in the UK). */
export const QUICK_TIMES = ['09:00', '12:00', '15:00'] as const;

const lengthLabel = (m: number) => (m % 60 === 0 ? `${m / 60} hr` : m > 60 ? `${Math.floor(m / 60)} hr ${m % 60} min` : `${m} min`);

/**
 * "Add to calendar" for a dated task (18 Oct 2026, web first). The user
 * picks how the EVENT goes in — all day, or a start time and a length —
 * without changing the task's own date. Google opens its own add-event page
 * filled in; "Apple / Outlook" downloads an .ics built on this device.
 * ZoneIn never reads a calendar. Web only: a file download inside the
 * native WebView is untested (§4).
 */
export function AddToCalendar({ task }: { task: Task }) {
  const [at, setAt] = useState<'allDay' | string>(() => defaultEventAt(task));
  const [minutes, setMinutes] = useState<number>(() => eventMinutes(task));
  const [pickOpen, setPickOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  if (isNative() || task.deadlineAt === undefined || task.done) return null;

  const choice: EventChoice = { at, minutes };
  const google = googleCalendarUrl(task, choice);
  const custom = at !== 'allDay' && !(QUICK_TIMES as readonly string[]).includes(at);

  const download = () => {
    const ics = icsFor(task, Date.now(), choice);
    if (!ics) return;
    const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = icsFileName(task);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setSaved(true);
  };

  const chip = (on: boolean) =>
    `rounded-full border px-2 py-0.5 text-[11px] font-bold transition-colors ${
      on
        ? 'border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-300'
        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-brand-300'
    }`;

  return (
    <div className="space-y-1.5" role="group" aria-label="Add to calendar">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="flex items-center gap-1 text-[11px] font-bold text-slate-600 dark:text-slate-300">
          <CalendarPlus size={11} /> Add to calendar
        </span>
        <span className="text-[11px] text-slate-500">At</span>
        <button type="button" aria-pressed={at === 'allDay'} onClick={() => setAt('allDay')} className={chip(at === 'allDay')}>
          All day
        </button>
        {QUICK_TIMES.map((t) => (
          <button key={t} type="button" aria-pressed={at === t} onClick={() => setAt(t)} className={chip(at === t)}>
            {t}
          </button>
        ))}
        {pickOpen || custom ? (
          <input
            type="time"
            aria-label="Event start time"
            value={custom ? at : ''}
            onChange={(e) => e.target.value && setAt(e.target.value)}
            className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-xs text-slate-900 dark:text-white"
          />
        ) : (
          <button type="button" onClick={() => setPickOpen(true)} className={chip(false)}>
            Pick a time
          </button>
        )}
      </div>

      {at !== 'allDay' && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-slate-500">Length</span>
          {lengthOptions(task).map((m) => (
            <button key={m} type="button" aria-pressed={minutes === m} onClick={() => setMinutes(m)} className={chip(minutes === m)}>
              {lengthLabel(m)}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {google && (
          <a href={google} target="_blank" rel="noreferrer" className={chip(false)}>
            Google
          </a>
        )}
        <button type="button" onClick={download} className={chip(false)}>
          Apple / Outlook (.ics)
        </button>
        {saved && (
          <span role="status" className="text-[10px] text-emerald-600 dark:text-emerald-400">
            Downloaded — open the file to add it to your calendar.
          </span>
        )}
      </div>
    </div>
  );
}
